import { CurrentUser } from "@/types/user"
import { DataType, dataTypeByFieldType, TemplateDetails, TimeZone } from "@/types"
import { CaseColumn, caseColumns } from "@/types/caseColumns"
import { DayOfMonth, DayOfWeek, Schedule, ScheduleFreq, ScheduleStatus, recordsToCreateValues, MultipleRecordInstance, QuarterMonth, scheduleHelpers } from "@/types/schedule"
import { formatDateMixin } from "@/mixins/formatDateMixin"
import moment from "moment"

import api from "@/services/api"

import DateTime from "@/components/Fields/DateTime.vue"
import MultiDatePicker from "@/components/Controls/MultiDatePicker.vue"
import CreateForTeam from "@/components/Config/Schedules/CreateForTeam.vue"
import NextRunTimes from "@/components/Config/Schedules/NextRunTimes.vue"
import FieldAndValue from "@/components/Controls/FieldAndValue.vue"
import ScheduleSetting from "@/types/scheduleSettings"
import { Folder, FolderType } from "@/types/folder"
import sortFieldValuesDictionary from "@/scripts/sortFieldValuesDictionary"
import _ from "lodash"

import StringValidation from "@/scripts/stringValidation"
import { TenantFeatures } from "@/types/tenantFeatures"
import { folderMixin } from "@/mixins/folderMixin"
import MultiCreateControl from "@/components/Config/Schedules/MultiCreateControl.vue"
import { Info } from "luxon"
import Vue from "vue"
import { FieldTypeId } from "@/types/fieldTypes"

export default class ScheduleEditService {
	public templates: TemplateDetails[] = []
	public fields: CaseColumn[] = []
	public timeZones: TimeZone[] = []
	public currentUser: CurrentUser | null = null

	public tenantTimeZones: TimeZone[] = []

	public scheduleSetting: ScheduleSetting = {
		id: undefined,
		tenantId: undefined,
		defaultFieldValues: {}
	};

	// The schedule being edited
	public currentSchedule: Schedule | null = null;

	// The initial template of the schedule being edited
	public initialTemplateId: string | null = null

	public selectedTemplate: TemplateDetails | null = null
	public availableFields: CaseColumn[] | null = null
	private folders: { [title:string]: Folder } = {}

	// tenant features
	public excludeWeekendsFeatureEnabled: boolean = false
	public excludeWeekendsForCustomDatesFeatureEnabled: boolean = false
	public scheduleHideStartTimeFeatureEnabled: boolean = false
	public scheduleHideFrequencyOnceAndTodayFeatureEnabled: boolean = false
	public scheduleShowTimeZoneColumnInTableFeatureEnabled: boolean = false
	public scheduleQuarterlyFrequencyEnabled: boolean = false
	public timeZoneAliasesFeatureEnabled: boolean = false
	public scheduleDisplayRunTimes: boolean = false
	public scheduleMakeFromUntilDatesMandatory: boolean = false

	public recordName: string | null = "Record"
	private minRunDate: Date | undefined = undefined
	private maxRunDate: Date | undefined = undefined
	private startTime: string | undefined = undefined
	private displayTimeZone: string | null = null

	private generatingCase: boolean = false
	private filterOutCaseColumns = ["created", "closed", "openForDays", "isClosed", "templateId", "caseNumber", "lastUpdated", "hiddenFromInsights", "deleted"]

	public getIsFeatureEnabled!: (feature: TenantFeatures) => boolean;

	private get scheduleFreqOptions () {
		var scheduleOptions = [
			{
				title: "Once",
				id: ScheduleFreq.Once
			},
			{
				title: "Daily",
				id: ScheduleFreq.Daily
			},
			{
				title: "Weekly",
				id: ScheduleFreq.Weekly
			},
			{
				title: "Monthly",
				id: ScheduleFreq.Monthly
			}
		]

		if (this.scheduleHideFrequencyOnceAndTodayFeatureEnabled) {
			scheduleOptions.removeWhere(option => option.id === ScheduleFreq.Once)
		}

		// Display the Quarterly schedule frequency if the feature is enabled or if the value has already been selected for an existing Schedule
		if (this.currentSchedule?.scheduleFreq === ScheduleFreq.Quarterly ||
			this.scheduleQuarterlyFrequencyEnabled) {
			scheduleOptions.push({
				title: "Quarterly",
				id: ScheduleFreq.Quarterly
			})
		}

		// Display the Custom Dates schedule frequency if the feature is enabled or if the value has already been selected for an existing Schedule
		if (this.currentSchedule?.scheduleFreq === ScheduleFreq.CustomDates ||
			this.currentUser?.tenantEnabledFeatures?.includes(TenantFeatures.ScheduleCustomDates)) {
			scheduleOptions.push({
				title: "Custom Dates",
				id: ScheduleFreq.CustomDates
			})
		}

		return scheduleOptions
	}

	public validateFolderSelection (item:Schedule) {
		if (this.currentUser?.tenantEnabledFeatures?.includes(TenantFeatures.ScheduleFolderMandatory)) {
			return item.folder ? null : "Please select a folder"
		}
		return null
	}

	private get dayOptions () {
		var days = [
			{
				title: "Monday",
				id: DayOfWeek.Monday
			},
			{
				title: "Tuesday",
				id: DayOfWeek.Tuesday
			},
			{
				title: "Wednesday",
				id: DayOfWeek.Wednesday
			},
			{
				title: "Thursday",
				id: DayOfWeek.Thursday
			},
			{
				title: "Friday",
				id: DayOfWeek.Friday
			},
			{
				title: "Saturday",
				id: DayOfWeek.Saturday
			},
			{
				title: "Sunday",
				id: DayOfWeek.Sunday
			}
		]

		if (this.excludeWeekendsFeatureEnabled && this.currentSchedule && this.currentSchedule.scheduleFreq === ScheduleFreq.Weekly && this.currentSchedule.excludeWeekends) {
			days.splice(-2)

			// Clear value if it is a weekend
			if (this.currentSchedule.dayOf === DayOfWeek.Saturday || this.currentSchedule.dayOf === DayOfWeek.Sunday) {
				this.currentSchedule.dayOf = undefined
			}
		}

		return days
	}

	private get quarterlyMonthOptions () {
		return Info.months().map((monthName, index) => {
			return { id: index + 1, title: monthName }
		})
	}

	public get availableTemplates () : {id: string, title: string}[] {
		if (!this.templates) return []

		// Only show templates that are marked as 'Available for Scheduling'
		var availableTemplates = this.templates.filter(t => t.templateSchedule && t.templateSchedule.schedulingAvailable && t.templateSchedule.schedulingPermissions)

		// Only show templates that the user has permissions for
		availableTemplates = availableTemplates.filter(t => t.templateSchedule!.schedulingPermissions!.length === 0 ||
			t.templateSchedule!.schedulingPermissions!.some((teamId: any) => this.currentUser?.teams.find((team: any) => team.id === teamId)))

		// Ensure the current schedules template is available
		if (this.currentSchedule && this.currentSchedule.templateId && !availableTemplates.find(t => t.id === this.currentSchedule!.templateId)) {
			var selectedTemplate = this.templates.find(t => t.id === this.currentSchedule!.templateId)
			if (selectedTemplate) {
				availableTemplates.push(selectedTemplate)
			}
		}

		return [
			...availableTemplates.map((template:any) => {
				return {
					id: template.id,
					title: template.title
				}
			})
		].sort((a, b) => a.title.localeCompare(b.title))
	}

	public initializeFeatures () {
		// tenant features
		if (this.getIsFeatureEnabled) {
			this.excludeWeekendsFeatureEnabled = this.getIsFeatureEnabled(TenantFeatures.SchedulingExcludeWeekends)
			this.excludeWeekendsForCustomDatesFeatureEnabled = this.excludeWeekendsFeatureEnabled &&
				this.getIsFeatureEnabled(TenantFeatures.SchedulingExcludeWeekendsForCustomDates)
			this.scheduleHideStartTimeFeatureEnabled = this.getIsFeatureEnabled(TenantFeatures.ScheduleHideStartTime)
			this.scheduleHideFrequencyOnceAndTodayFeatureEnabled = this.getIsFeatureEnabled(TenantFeatures.ScheduleHideFrequencyOnceAndToday)
			this.scheduleQuarterlyFrequencyEnabled = this.getIsFeatureEnabled(TenantFeatures.ScheduleQuarterlyFrequency)
			this.scheduleShowTimeZoneColumnInTableFeatureEnabled = this.getIsFeatureEnabled(TenantFeatures.ScheduleTimeZoneColumn)
			this.timeZoneAliasesFeatureEnabled = this.getIsFeatureEnabled(TenantFeatures.TimezoneAliases)
			this.scheduleDisplayRunTimes = this.getIsFeatureEnabled(TenantFeatures.ScheduleDisplayRunTimes)
			this.scheduleMakeFromUntilDatesMandatory = this.getIsFeatureEnabled(TenantFeatures.ScheduleMakeFromUntilDatesMandatory)
		}
	}

	public templateChanged (selectedTemplateId: string | null, applyDefaultValues: boolean) {
		this.selectedTemplate = selectedTemplateId
			? this.templates.find(t => t.id === selectedTemplateId) || null
			: null

		// Set available Fields
		this.availableFields = []
		if (selectedTemplateId) {
			if (this.selectedTemplate) {
				var fieldsInTemplate = this.fields.filter(field => caseColumns.find(f => f.key === field.key) || this.selectedTemplate!.fields.find(f => f.field.id === field.key))

				// Check if the fields are available in template scheduling
				if (this.selectedTemplate!.templateSchedule && this.selectedTemplate!.templateSchedule!.schedulingFields!.length !== 0) {
					this.availableFields = fieldsInTemplate.filter(field => this.selectedTemplate!.templateSchedule!.schedulingFields!.find(f => f === field.key))
				} else {
					this.availableFields = fieldsInTemplate
				}
			} else {
				// If template can't be found, allow anything just in case
				this.availableFields = this.fields
			}
		}

		// exclude fields that do not support default values
		const excludedFieldDataTypes: string[] = [FieldTypeId.Calculated, FieldTypeId.FieldMetrics]
		this.availableFields = this.availableFields.filter(f => !excludedFieldDataTypes.includes(f.dataType))

		if (!this.currentSchedule!.fields) {
			return
		}

		if (selectedTemplateId) {
			// Remove any fields that are not available
			Object.keys(this.currentSchedule!.fields).forEach(fieldId => {
				if (!this.availableFields!.find(f => f.key === fieldId)) {
					delete this.currentSchedule!.fields[fieldId]
				}
			})
		}

		if (applyDefaultValues) {
			// Apply field defaults if the field has not been set
			this.availableFields!.forEach(f => {
				if (!this.currentSchedule!.fields[f.key]) {
					if (this.selectedTemplate && this.selectedTemplate.templateSchedule && this.selectedTemplate.templateSchedule!.defaultValues![f.key]) {
						// Apply Template Specific default values
						this.currentSchedule!.fields[f.key] = this.selectedTemplate!.templateSchedule!.defaultValues![f.key]
					} else if (this.scheduleSetting!.defaultFieldValues[f.key]) {
						// Apply generic default values
						this.currentSchedule!.fields[f.key] = this.scheduleSetting!.defaultFieldValues[f.key]
					}
				}
			})
		}

		// Sort current schedule fields.
		if (this.currentSchedule && this.currentSchedule.fields && this.selectedTemplate && this.selectedTemplate.fields) {
			var defaults = this.currentSchedule.fields

			if (defaults && this.selectedTemplate.fields.length > 0 && this.selectedTemplate.sections.length > 0) {
				this.currentSchedule.fields = sortFieldValuesDictionary(defaults, this.selectedTemplate.fields, this.selectedTemplate.sections)
			}
		}
	}

	private get availableMultiCreateFields () {
		if (!this.selectedTemplate?.templateSchedule?.multipleRecordFields || this.selectedTemplate?.templateSchedule?.multipleRecordFields.length === 0) {
			return this.availableFields
		}
		return this.availableFields?.filter(f => this.selectedTemplate?.templateSchedule?.multipleRecordFields?.includes(f.key))
	}

	public minRunDateChanged (selectedDate: Date) {
		this.minRunDate = selectedDate
	}

	public maxRunDateChanged (selectedDate: Date) {
		this.maxRunDate = selectedDate
	}

	public startTimeChanged (selectedTime: string) {
		this.startTime = selectedTime || "00:00"
		if (this.currentSchedule) {
			this.currentSchedule.startTime = selectedTime || "00:00"
		}
	}

	public scheduleFrequencyChanged (scheduleFrequency: any) {
		if (!this.excludeWeekendsForCustomDatesFeatureEnabled) {
			if (this.currentSchedule && scheduleFrequency === ScheduleFreq.CustomDates) {
				this.currentSchedule.excludeWeekends = false
			}
		}
	}

	public customDatesExcludeWeekendsChanged (excludeWeekends: boolean) {
		if (this.excludeWeekendsForCustomDatesFeatureEnabled) {
			if (this.currentSchedule) {
				this.currentSchedule.excludeWeekends = excludeWeekends
			}
		}
	}

	public timeZoneChanged (timeZone: string) {
		if (this.currentSchedule) {
			this.currentSchedule.timeZoneId = timeZone
		}

		this.displayTimeZone = this.timeZoneAliasesFeatureEnabled
			? this.tenantTimeZones.find(tz => tz.id === timeZone)?.title ?? timeZone
			: timeZone
	}

	public scheduleDayOfChanged (dayOf: number) {
		if (this.currentSchedule) {
			if (this.currentSchedule.dayOf === undefined || this.currentSchedule.dayOf === null) {
				Vue.delete(this.currentSchedule, "dayOf")
			}
		}
	}

	public showMaxMinDatesInModal (item:Schedule) {
		if (this.currentUser?.tenantEnabledFeatures?.includes(TenantFeatures.ScheduleFrequencyDatesCheck)) {
			return (!!item.scheduleFreq && (item.scheduleFreq !== ScheduleFreq.Once))
		}
		return (item.scheduleFreq !== ScheduleFreq.Once)
	}

	public get columns () : any[] {
		return [
			{
				key: "folderOrExpandable",
				label: "Folder",
				sortable: false,
				inModal: () => false,
				inTable: true,
				hasTemplate: true,
				filterByFormatted: true,
				formatter: (value: ScheduleStatus, key: string, item: Schedule) => {
					// Value to use for filtering
					return this.friendlyFolderName(item)
				}
			},
			{
				key: "title",
				label: "Title",
				sortable: true,
				errors: (item: any) => StringValidation.validateTitle(item.title)
			},
			{
				key: "templateTitle",
				label: "Template",
				dataType: "list",
				valueColumn: "templateId",
				options: this.availableTemplates,
				errors: (item: any) => (item.templateId ? null : "Please select a template"),
				sortable: true,
				inTable: true,
				tooltip: "Ensure that all relevant templates have been marked as 'Available for Scheduling'"
			},
			{
				key: "status",
				label: "Status",
				sortable: true,
				inTable: true,
				inModal: () => false,
				hasTemplate: true,
				filterByFormatted: true,
				formatter: (value: ScheduleStatus, key: string, item: Schedule) => this.getStatusDescription(item)
			},
			{
				key: "folder",
				label: "Folder",
				dataType: "list",
				options: this.getPermittedSelectionFolders(),
				sortable: true,
				inTable: false,
				inModal: () => true,
				errors: (item: Schedule) => (this.validateFolderSelection(item))
			},
			{
				key: "lastUpdated",
				label: "Last Updated",
				sortable: true,
				dataType: "date-time",
				inModal: () => false,
				inTable: false
			},
			{
				key: "lastUpdatedBy.name",
				label: "Last Updated By",
				sortable: true,
				dataType: "text",
				inModal: () => false,
				inTable: false
			},
			{
				key: "timeZoneId",
				label: this.isMultiInstanceWithTimezones() ? "Default Time Zone * " : "Time Zone",
				sortable: true,
				dataType: "list",
				valueColumn: "timeZoneId",
				options: this.tenantTimeZones,
				errors: (item: Schedule) => (item.timeZoneId ? null : "Please select a time zone"),
				inTable: this.scheduleShowTimeZoneColumnInTableFeatureEnabled,
				formatter: (value: string) => {
					return this.timeZoneAliasesFeatureEnabled
						? this.tenantTimeZones.find(tz => tz.id === value)?.title ?? value
						: value
				}
			},
			{
				key: "timeZoneMultiInstanceWarning",
				label: `* This schedule is set with multiple 'Records To Create'; each instance has its own timezone (set below). 
					The timezone above is a default value used if not overridden.`,
				inTable: false,
				inModal: () => this.isMultiInstanceWithTimezones(),
				dataType: "none",
				small: true
			},
			{
				key: "scheduleFreq",
				label: "Schedule Frequency",
				sortable: true,
				dataType: "list",
				valueColumn: "scheduleFreq",
				options: this.scheduleFreqOptions,
				errors: (item: any) => (item.scheduleFreq ? null : "Please select a schedule frequency"),
				hasTemplate: true,
				inTable: true,
				filterByFormatted: true,
				formatter: (value: ScheduleFreq, key: string, item: Schedule) => this.getScheduleFrequencyDesc(item),
				tooltip: this.frequencyTooltip
			},
			{
				key: "excludeWeekends",
				label: "Exclude Weekends",
				dataType: "checkbox",
				inModal: (item: any) => (item.scheduleFreq && item.scheduleFreq !== ScheduleFreq.CustomDates),
				valueColumn: "excludeWeekends",
				inTable: true,
				tooltip: this.excludeWeekendsTooltip
			},
			{
				key: "month",
				label: "Start Month",
				sortable: true,
				dataType: "list",
				valueColumn: "month",
				options: (item: any) => (this.quarterlyMonthOptions),
				inModal: (item: any) => (item.scheduleFreq === ScheduleFreq.Quarterly),
				errors: (item: Schedule) => item.scheduleFreq !== ScheduleFreq.Quarterly || item.month ? null : "Please select a Month",
				inTable: false
			},
			{
				key: "dayOf",
				label: "Day",
				sortable: true,
				dataType: "list",
				valueColumn: "dayOf",
				options: (item: any) => ((item.scheduleFreq === ScheduleFreq.Weekly) ? this.dayOptions : DayOfMonth),
				inModal: (item: any) => (item.scheduleFreq === ScheduleFreq.Weekly || item.scheduleFreq === ScheduleFreq.Monthly || item.scheduleFreq === ScheduleFreq.Quarterly),
				errors: (item: any) => this.validateDaysOfInput(item),
				inTable: false
			},
			{
				key: "date",
				label: "Date",
				sortable: true,
				dataType: "date",
				dynamicComponent: DateTime,
				inModal: (item: any) => (item.scheduleFreq === ScheduleFreq.Once),
				componentProperties: {
					includeYesterday: false,
					includeToday: !this.scheduleHideFrequencyOnceAndTodayFeatureEnabled,
					enterAsUTC: true
				},
				errors: (item: any) => this.validateOnceDateInput(item),
				inTable: false
			},
			{
				key: "minRunDate",
				label: "From",
				sortable: true,
				dataType: "date",
				dynamicComponent: DateTime,
				inModal: (item: Schedule) => this.showMaxMinDatesInModal(item),
				componentProperties: {
					includeYesterday: false,
					includeToday: !this.scheduleHideFrequencyOnceAndTodayFeatureEnabled,
					enterAsUTC: true
				},
				errors: (schedule: Schedule) => this.validateFromUntilDates(schedule, "from") || this.validateMaxMinDates(schedule),
				hasTemplate: true,
				inTable: true,
				filterByFormatted: true,
				formatter: (value: string) => formatDateMixin.methods.displayUtcDateOnlyFromISOString(value)
			},
			{
				key: "maxRunDate",
				label: "Until",
				sortable: true,
				dataType: "date",
				dynamicComponent: DateTime,
				inModal: (item: any) => this.showMaxMinDatesInModal(item),
				componentProperties: {
					includeYesterday: false,
					includeToday: false,
					enterAsUTC: true
				},
				errors: (schedule: Schedule) => this.validateFromUntilDates(schedule, "until") || this.validateMaxMinDates(schedule),
				inTable: true,
				hasTemplate: true,
				filterByFormatted: true,
				formatter: (value: string) => formatDateMixin.methods.displayUtcDateOnlyFromISOString(value)
			},
			{
				key: "dates",
				label: "Custom Dates",
				sortable: true,
				dataType: "date-list",
				dynamicComponent: MultiDatePicker,
				inModal: (item: any) => (item.scheduleFreq === ScheduleFreq.CustomDates),
				componentProperties: {
					startDate: this.minRunDate,
					endDate: this.maxRunDate,
					initialExcludeWeekends: this.currentSchedule?.excludeWeekends ?? false
				},
				eventHandlers: {
					"exclude-weekends-changed": (newValue: boolean) => this.customDatesExcludeWeekendsChanged(newValue)
				},
				errors: (item: any) => this.validateCustomDates(item),
				inTable: false
			},
			{
				key: "recordsStartTimeWarning",
				label: "Note: All scheduled services are created at midnight in the selected timezone(s).",
				inTable: false,
				inModal: () => this.scheduleHideStartTimeFeatureEnabled,
				dataType: "none",
				small: true
			},
			{
				key: "recordsStartTimeWarningSpecified",
				label: "Note: All scheduled services are processed at the specified start time in the selected timezone.",
				inTable: false,
				inModal: () => !this.scheduleHideStartTimeFeatureEnabled,
				dataType: "none",
				small: true
			},
			{
				key: "recordsToCreate",
				label: `${this.recordName}s To Create`,
				sortable: true,
				dataType: "list",
				valueColumn: "recordsToCreate",
				options: recordsToCreateValues,
				inModal: (item: any) => (this.currentUser?.tenantEnabledFeatures?.includes(TenantFeatures.ScheduleMultipleRecords)),
				inTable: this.currentUser?.tenantEnabledFeatures?.includes(TenantFeatures.ScheduleMultipleRecords),
				tooltip: `Amount of ${this.recordName}s that will be created per day`,
				errors: (item: Schedule) => (item.recordsToCreate ? null : "Please select a value")
			},
			{
				key: "multipleRecordInstances",
				label: "",
				dynamicComponent: MultiCreateControl,
				inModal: (item: any) => (this.currentUser?.tenantEnabledFeatures?.includes(TenantFeatures.ScheduleMultipleRecords) && item.recordsToCreate > 1),
				componentProperties: {
					value: this.currentSchedule?.multipleRecordInstances,
					recordsToCreate: this.currentSchedule?.recordsToCreate,
					multipleRecordFields: this.availableMultiCreateFields,
					validationErrors: this.validateMultiCreate(this.currentSchedule!),
					useTimezones: this.scheduleHideStartTimeFeatureEnabled,
					scheduleMainTimezone: this.currentSchedule?.timeZoneId,
					timezones: this.tenantTimeZones
				},
				eventHandlers: {
					amountChanged: (amount: number) => this.currentSchedule!.recordsToCreate! = amount,
					input: (value: MultipleRecordInstance[]) => this.currentSchedule!.multipleRecordInstances! = value
				},
				inTable: false,
				errors: (item: any) => this.validateMultiCreate(item)
			},
			{
				key: "startTime",
				label: this.displayTimeZone ? `Start Time in ${this.displayTimeZone}` : "Start Time",
				sortable: true,
				dataType: "time",
				inModal: (item: any) => !this.scheduleHideStartTimeFeatureEnabled,
				inTable: !this.scheduleHideStartTimeFeatureEnabled,
				errors: (item: any) => ((item.recordsToCreate > 1 || item.startTime) ? null : "Please enter a start time")
			},
			{
				key: "endTime",
				label: "End Time",
				sortable: true,
				dataType: "time",
				inModal: (item: any) => (item.recordsToCreate === 1) && !this.scheduleHideStartTimeFeatureEnabled,
				inTable: !this.scheduleHideStartTimeFeatureEnabled
			},
			{
				key: "nextRunTime", // To display next run in table
				label: "Next Run Time",
				inTable: this.scheduleDisplayRunTimes,
				valueColumn: "nextRun",
				formatter: (value: string) => formatDateMixin.methods.displayDateOnlyFromISOString(value),
				hasTemplate: true,
				inModal: () => false
			},
			{
				key: "nextRunTimes", // To display list of upcoming runs in modal
				label: this.displayTimeZone ? `Next Run Time(s) in ${this.displayTimeZone}` : "Next Run Time(s)",
				inTable: false,
				dynamicComponent: NextRunTimes,
				formatter: (value: string) => formatDateMixin.methods.displayDateOnlyFromISOString(value),
				inModal: () => this.scheduleDisplayRunTimes
			},
			{
				key: "createdBy.name",
				label: "Created By",
				sortable: true,
				dataType: "text",
				inModal: () => false,
				inTable: true,
				hasTemplate: true
			},
			{
				key: "created",
				label: "Created",
				sortable: true,
				dataType: "date-time",
				inModal: () => false,
				inTable: true,
				hasTemplate: true,
				filterByFormatted: true,
				formatter: (value: string) => formatDateMixin.methods.displayDateFromISOString(value)
			},
			{
				key: "newSchedule",
				label: "",
				sortable: false,
				hasTemplate: true,
				inTable: true,
				inModal: () => false
			},
			{
				key: "bulkCreateForTeams",
				label: "Bulk Create for Roles",
				inTable: false,
				dynamicComponent: CreateForTeam
			},
			{
				key: "bulkCreateUserRole",
				valueColumn: "bulkCreateUserRole",
				label: "Bulk Create - User Role",
				inTable: false,
				inModal: (schedule: Schedule) => schedule.templateId && schedule.bulkCreateForTeams && schedule.bulkCreateForTeams.length > 0,
				dataType: "list",
				options: (item: any) => item.templateId
					? this.templates.findAndGet(t => t.id === item.templateId, t => t.fields.filter(f => f.field.dataType === "user-role")
						.map(f => {
							return {
								id: f.field.id,
								title: f.field.title
							}
						}),
					[])
					: []
			},
			{
				key: "bulkCreateSubRole",
				valueColumn: "bulkCreateSubRole",
				label: "Bulk Create - Sub-Role",
				inTable: false,
				inModal: (schedule: Schedule) => schedule.templateId && this.subRolesEnabled(schedule),
				dataType: "list",
				errors: (schedule: Schedule) => this.validateSubRoles(schedule),
				options: (schedule: Schedule) =>
					this.fields
						.findAndGet(f => f.key === schedule.bulkCreateUserRole,
							f => f.fieldExtensions && f.fieldExtensions.subroles ? f.fieldExtensions.subroles.map((subrole: { title: string }) => {
								return {
									id: subrole.title,
									title: subrole.title
								}
							}) : [], [])
			},
			{
				key: "fields",
				label: "Fields",
				inTable: false,
				dynamicComponent: FieldAndValue,
				componentProperties: {
					fields: this.availableFields,
					utcDates: true
				}
			}
		]
	}

	public getPermittedFolders () : { [title:string] : Folder } {
		const userTeamIds = this.currentUser?.teams?.map(t => t.id)

		// folders where some of the user's teams are included in the folder's teams
		const permittedFolders = _.pickBy(this.folders, folder =>
			(!folder.teams || folder.teams.length === 0) ||
			folder.teams.some(folderTeamId => userTeamIds?.includes(folderTeamId)))
		return permittedFolders
	}

	// needs a hack like this somewhere as saving will default to saving the folder.id to schedule.folder, but it expects the folder title
	private getPermittedSelectionFolders () : {id: string, title: string }[] {
		const permittedFolders: { [title:string] : Folder } = this.getPermittedFolders()

		// Get the list of folder objects
		const folderList: Folder[] = Object.entries(permittedFolders).map(([key, value]) => value)

		const objectList: {id: string, title: string}[] = Object.values(permittedFolders)
			.map(folder => {
				// Get the full path of the folder
				const folderTitle = folderMixin.methods.getPathToRootFolder(folder, folderList, true)
				return { id: folderTitle, title: folderTitle }
			})

		// Return the sorted list
		return objectList.sort(function (a, b) {
			return a.title.localeCompare(b.title)
		})
	}

	private validateMaxMinDates (item: Schedule) : string | null {
		if (!(item.minRunDate && item.maxRunDate)) {
			return null
		}

		const minRunDate = new Date(item.minRunDate)
		const maxRunDate = new Date(item.maxRunDate)
		minRunDate.setHours(0, 0, 0, 0)
		maxRunDate.setHours(0, 0, 0, 0)

		if (new Date(minRunDate) > new Date(maxRunDate)) {
			return "Minimum schedule date cannot be after maximum"
		}

		return null
	}

	private validateCustomDates (item: Schedule) : string | null {
		if (item.scheduleFreq !== ScheduleFreq.CustomDates) {
			return null
		}

		if (!item.minRunDate || !item.maxRunDate) {
			return "Please enter valid minumum and maximum schedule dates to select custom dates"
		}
		if (item.minRunDate > item.maxRunDate) {
			return "Please ensure the minimum schedule date is not after the maximum schedule date"
		}

		// Validate if the end date is more than 3 months after the start date
		if (item.maxRunDate > new Date(new Date(item.minRunDate).setMonth(new Date(item.minRunDate).getMonth() + 3))) {
			return "The maximum schedule date cannot be more than 3 months after the minimum schedule date for custom dates"
		}

		if (item.dates == null || item.dates.length < 1) {
			return "Please select at least 1 valid date from the calendar"
		}

		return null
	}

	private validateMultiCreate (item: Schedule) : string | null {
		if (!item) {
			return null
		}

		if (!this.currentUser?.tenantEnabledFeatures?.includes(TenantFeatures.ScheduleMultipleRecords) || !item.recordsToCreate || item.recordsToCreate === 1) {
			return null
		}

		if (this.scheduleHideStartTimeFeatureEnabled) {
			if (!item.multipleRecordInstances || item.multipleRecordInstances.find(i => !i.timezone || i.timezone === "")) {
				return "Please ensure all instances have a timezone set"
			}
		} else {
			if (!item.multipleRecordInstances || item.multipleRecordInstances.find(i => i.startTime === null || i.startTime === "")) {
				return "Please ensure all times are specified"
			}
		}

		return null
	}

	private subRolesEnabled (schedule: Schedule) : boolean {
		return schedule.bulkCreateForTeams && schedule.bulkCreateForTeams.length > 0 && schedule.bulkCreateUserRole &&
					this.fields.findAndGet(f => f.key === schedule.bulkCreateUserRole, f => f.fieldExtensions && f.fieldExtensions.hasSubroles, false)
	}

	private validateSubRoles (schedule: Schedule) : string | null {
		if (!this.subRolesEnabled(schedule)) {
			return null
		}

		if (schedule.bulkCreateSubRole) {
			return null
		}

		return "Please select a sub-role"
	}

	private validateDaysOfInput (item: Schedule) : string | null {
		if (item.scheduleFreq === ScheduleFreq.Weekly) {
			if (item.dayOf === null || item.dayOf === undefined || item.dayOf < 0 || item.dayOf > 6) {
				return "Please select a valid Day of Week"
			}

			if (this.excludeWeekendsFeatureEnabled && item.excludeWeekends && (item.dayOf === DayOfWeek.Sunday || item.dayOf === DayOfWeek.Saturday)) {
				return "Cannot select a weekend"
			}
		}

		if (item.scheduleFreq === ScheduleFreq.Monthly || item.scheduleFreq === ScheduleFreq.Quarterly) {
			if (item.dayOf === null || item.dayOf === undefined || item.dayOf < 1 || item.dayOf > 31) {
				return "Please select a valid Day of Month"
			}
		}

		if (!(item.minRunDate && item.maxRunDate)) {
			return null
		}

		if (item.scheduleFreq === ScheduleFreq.Weekly) {
			const dates = this.getDatesBetweenTwoDates(item.minRunDate, item.maxRunDate)

			const daysOfWeek = dates.map(c => c.getDay())
			const dayOfIsInRange = (item.dayOf === DayOfWeek.Sunday || item.dayOf) && daysOfWeek.includes(item.dayOf) // 0 is Sunday, so it is valid

			if (!dayOfIsInRange) {
				return "Day of Week selected is not within maximum and minimum dates"
			}
		}

		if (item.scheduleFreq === ScheduleFreq.Monthly) {
			const dates = this.getDatesBetweenTwoDates(item.minRunDate, item.maxRunDate)
			const daysOfMonth = dates.map(c => c.getDate())

			const dayOfIsInRange = item.dayOf && daysOfMonth.includes(item.dayOf)

			if (!dayOfIsInRange) {
				return "Day of Month selected is not within maximum and minimum dates"
			}
		}

		return null
	}

	validateOnceDateInput (item: Schedule) : string | null {
		if (item.scheduleFreq !== ScheduleFreq.Once) {
			return null
		}

		item.minRunDate = null
		item.maxRunDate = null

		if (!item.date) {
			return "Please select a date"
		}

		const date = new Date(item.date)

		if (!moment(date).isSameOrAfter(moment().add(this.scheduleHideFrequencyOnceAndTodayFeatureEnabled ? 1 : 0, "days").startOf("day"))) {
			return "Please select a future date"
		}

		if (this.excludeWeekendsFeatureEnabled && item.excludeWeekends && (date.getDay() === DayOfWeek.Sunday || date.getDay() === DayOfWeek.Saturday)) {
			return "Cannot select a weekend"
		}
		return null
	}

	private validateFromUntilDates (schedule: Schedule, dateType: "from" | "until") : string | null {
		if (this.scheduleMakeFromUntilDatesMandatory) {
			if (dateType === "from" && !schedule.minRunDate) {
				return "Please select a 'From' date"
			}
			if (dateType === "until" && !schedule.maxRunDate) {
				return "Please select an 'Until' date"
			}
		}
		return null
	}

	private getDatesBetweenTwoDates (startDate: Date, stopDate: Date) : Date[] {
		const dateArray: Date[] = []
		let current = moment(startDate)
		const stop = moment(stopDate)
		while (current <= stop) {
			dateArray.push(current.toDate())
			current = current.add(1, "days")
		}
		return dateArray
	}

	friendlyFolderName (schedule: Schedule) {
		if (schedule.folder === null || schedule.folder === undefined) {
			return "None"
		}
		return schedule.folder
	}

	getStatusDescription (schedule: Schedule): string {
		if (!schedule || schedule.status === null || schedule.status === undefined) return ""

		return ScheduleStatus[schedule.status]
	}

	getScheduleFrequencyDesc (schedule: Schedule): string {
		if (!schedule || schedule.scheduleFreq === null || schedule.scheduleFreq === undefined) return ""

		const selectedFrequency = this.scheduleFreqOptions.filter(x => x.id === schedule.scheduleFreq)[0]
		if (!selectedFrequency) {
			return ""
		}

		switch (schedule.scheduleFreq) {
			case ScheduleFreq.Daily:
				return selectedFrequency.title
			case ScheduleFreq.Weekly:
				const baseDate = new Date(2017, 0, 2)
				baseDate.setDate(baseDate.getDate() + schedule.dayOf! - 1)
				return selectedFrequency.title + " (" + baseDate.toLocaleDateString(window.navigator.language, { weekday: "long" }) + ")"
			case ScheduleFreq.Monthly:
				return selectedFrequency.title + " (" + schedule.dayOf! + ")"
			case ScheduleFreq.Quarterly:
				// Display a list of all the months the schedule will run for e.g. Jan,Apr,Jul,Oct
				var months = this.quarterlyMonthOptions.filter(i => scheduleHelpers.isOnQuarterlyDate(schedule, i.id)).map(i => {
					let monthDate = new Date()
					monthDate.setMonth(i.id - 1)
					return monthDate.toLocaleString("en-us", { month: "short" })
				}).join(",")

				return selectedFrequency.title + " (" + schedule.dayOf! + " " + months + ")"
			case ScheduleFreq.Once:
				return new Date(schedule.date!).toDateString()
			case ScheduleFreq.CustomDates:
				return selectedFrequency.title
		}

		return selectedFrequency.title
	}

	private get frequencyTooltip () {
		if (this.currentSchedule) {
			switch (this.currentSchedule.scheduleFreq) {
				case ScheduleFreq.Quarterly:
					return "Scheduled every 3 months"
			}
		}

		return "How often the schedule will be run"
	}

	private get excludeWeekendsTooltip () {
		if (this.currentSchedule) {
			switch (this.currentSchedule.scheduleFreq) {
				case ScheduleFreq.Once || ScheduleFreq.Weekly:
					return "Prevents weekends from being selected"
				case ScheduleFreq.Monthly || ScheduleFreq.Quarterly:
					return "Schedules that fall on weekends will be moved to the next weekday"
			}
		}

		return "Exclude weekends from the schedule"
	}

	public async load (currentUser: CurrentUser, onlyLoadUser: boolean = false) {
		const currentUserLoad = currentUser ? Promise.resolve(this.currentUser = currentUser) : api.me().then(user => this.currentUser = user)

		if (onlyLoadUser) {
			await Promise.all([currentUserLoad])
			return
		}

		const templateLoad = api.templatesWithFields().then(templates => this.templates = templates)
		const timezoneLoad = api.timeZonesGet().then(timeZones => this.timeZones = timeZones)

		const foldersLoad = this.loadFolders()

		const fieldLoad = api.fields().then(fields => {
			if (!fields) {
				this.fields = []
				return
			}

			// Map the default and created fields
			this.fields = [
				...caseColumns.filter(f => this.filterOutCaseColumns.includes(f.key) === false),
				...(fields ? fields
					.map(f => {
						return {
							label: f.title,
							description: f.description,
							key: f.id!,
							fieldId: f.id,
							dataType: f.dataType!,
							dataSourceId: f.dataSourceId,
							dynamicField: true,
							fieldExtensions: f.extensions,
							fieldDataType: f.dataType === "calculated"
								? DataType.number
								: dataTypeByFieldType[f.dataType!]
									? dataTypeByFieldType[f.dataType!]
									: DataType.string
						}
					})
					: [])
			]
		})

		await Promise.all([templateLoad, timezoneLoad, foldersLoad, fieldLoad, currentUserLoad])
	}

	private async loadFolders () {
		const folders: Folder[] = await api.foldersGet(FolderType.Schedule)
		this.folders = folders.toDictionary("title")
	}

	public async saveSchedule (schedule: Schedule): Promise<void> {
		if (schedule.templateId) {
			schedule.templateTitle = this.templates.find(x => x.id === schedule.templateId)!.title
		}

		if (schedule.id) {
			await api.updateSchedule(schedule)
		} else {
			await api.createSchedule(schedule)
		}
	}

	public async deleteSchedule (schedule: Schedule): Promise<void> {
		await api.deleteSchedule(schedule)
	}

	private isMultiInstanceWithTimezones (): boolean {
		return (this.scheduleHideStartTimeFeatureEnabled && !!this.currentSchedule?.recordsToCreate && this.currentSchedule!.recordsToCreate > 1)
	}
}
